import type { Asset } from '../model/asset-types';
import { rewriteOssPublicUrlForDev } from '../upload/model/upload-service';
import {
  ossThumbnailTiers,
  type OssThumbnailTier,
  buildOssWatermarkUrl,
  buildOssThumbnailUrl,
  buildVideoFirstFrameUrl,
  buildVideoPosterPreviewUrl,
} from '@/shared/lib/oss-url-utils'

type AssetUrlLike = Pick<Asset, 'originalUrl' | 'previewUrl' | 'mimeType' | 'width' | 'height'> & { type?: Asset['type'] };

type AssetCanvasPreviewOptions = {
  devicePixelRatio?: number;
  displayHeight: number;
  displayWidth: number;
  maxTierWidth?: number;
  zoom?: number;
};

export type AssetCanvasPreviewResolution = {
  url?: string;
  sourceUrl?: string;
  tierWidth?: number;
  tierHeight?: number;
};

const MAX_EFFECTIVE_DEVICE_PIXEL_RATIO = 2;
const DUH_OSS_HOSTS = new Set(['img.duh.com.cn', 'hwimg.duh.com.cn']);
const QIANLU_OSS_HOSTS = new Set(['canvas.qianlu.cc', 'res.qianlu.net']);

function isFinitePositiveNumber(value?: number) {
  return Number.isFinite(value) && (value ?? 0) > 0;
}

function isLikelyImageUrl(url: string) {
  return /\.(avif|gif|jpe?g|png|webp)(\?|#|$)/i.test(url) || url.includes('x-oss-process=image/');
}

function isLikelyVideoUrl(url: string) {
  return /\.(mp4|mov|m4v|webm|ogg)(\?|#|$)/i.test(url) || url.includes('x-oss-process=video/snapshot');
}

function normalizeOssHost(hostname: string) {
  if (hostname === 'hwimg.duh.com.cn') {
    return 'img.duh.com.cn';
  }
  return hostname;
}

function isOssProcessableImageUrl(url: string) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }

  try {
    const parsed = new URL(url);
    const hostname = normalizeOssHost(parsed.hostname);
    if (DUH_OSS_HOSTS.has(hostname) || QIANLU_OSS_HOSTS.has(hostname)) {
      return isLikelyImageUrl(url);
    }
  } catch {
    return false;
  }

  return false;
}

function isOssProcessableVideoUrl(url: string) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }

  try {
    const parsed = new URL(url);
    const hostname = normalizeOssHost(parsed.hostname);
    if (DUH_OSS_HOSTS.has(hostname) || QIANLU_OSS_HOSTS.has(hostname)) {
      return isLikelyVideoUrl(url);
    }
  } catch {
    return false;
  }

  return false;
}

function resolveOssThumbnailTier({ devicePixelRatio, displayHeight, displayWidth, zoom }: AssetCanvasPreviewOptions) {
  const effectiveZoom = isFinitePositiveNumber(zoom) ? zoom! : 1;
  const effectiveDevicePixelRatio = Math.min(
    MAX_EFFECTIVE_DEVICE_PIXEL_RATIO,
    isFinitePositiveNumber(devicePixelRatio) ? devicePixelRatio! : 1,
  );
  const targetWidth = displayWidth * effectiveZoom * effectiveDevicePixelRatio;
  const targetHeight = displayHeight * effectiveZoom * effectiveDevicePixelRatio;

  return (
    ossThumbnailTiers.find((tier) => tier.width >= targetWidth && tier.height >= targetHeight)
    ?? ossThumbnailTiers.find((tier) => tier.id === 'T4')
    ?? ossThumbnailTiers[0]
  );
}

function clampOssThumbnailTierMaxWidth(tier: OssThumbnailTier, maxTierWidth?: number) {
  if (!isFinitePositiveNumber(maxTierWidth)) {
    return tier;
  }

  const constrainedTier =
    [...ossThumbnailTiers]
      .sort((a, b) => a.width - b.width)
      .find((candidate) => candidate.width >= maxTierWidth!)
    ?? [...ossThumbnailTiers].sort((a, b) => b.width - a.width)[0];

  return tier.width > constrainedTier.width ? constrainedTier : tier;
}

export function resolveAssetPreviewUrl(asset?: AssetUrlLike | null) {
  return asset ? rewriteOssPublicUrlForDev(asset.previewUrl ?? asset.originalUrl ?? "") : undefined;
}

export function resolveAssetOriginalUrl(asset?: AssetUrlLike | null) {
  return asset ? rewriteOssPublicUrlForDev(asset.originalUrl ?? asset.previewUrl ?? "") : undefined;
}

/** 播放地址默认使用原始地址 */
export const resolveAssetPlaybackUrl = resolveAssetOriginalUrl;

export function resolveAssetCanvasPreviewUrl(asset: AssetUrlLike | null | undefined, options?: AssetCanvasPreviewOptions) {
  return resolveAssetCanvasPreview(asset, options).url;
}

export function resolveAssetCanvasPreview(
  asset: AssetUrlLike | null | undefined,
  options?: AssetCanvasPreviewOptions,
): AssetCanvasPreviewResolution {
  const previewUrl = resolveAssetPreviewUrl(asset);
  const originalUrl = resolveAssetOriginalUrl(asset);

  // 补全默认参数逻辑收拢到此处
  const devicePixelRatio = options?.devicePixelRatio ?? (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1);
  const zoom = options?.zoom ?? 1;
  const effectiveOptions = options ? { ...options, devicePixelRatio, zoom } : undefined;

  if (asset?.mimeType?.startsWith('video/') || asset?.type === 'video') {
    const videoSourceUrl = originalUrl ?? previewUrl;
    if (!videoSourceUrl) {
      return {
        url: undefined,
        sourceUrl: undefined,
      };
    }

    const firstFrameUrl = buildVideoFirstFrameUrl(videoSourceUrl);
    if (
      !effectiveOptions
      || !isFinitePositiveNumber(effectiveOptions.displayWidth)
      || !isFinitePositiveNumber(effectiveOptions.displayHeight)
      || !isOssProcessableVideoUrl(videoSourceUrl)
    ) {
      return {
        url: firstFrameUrl,
        sourceUrl: videoSourceUrl,
      };
    }

    const tier = clampOssThumbnailTierMaxWidth(resolveOssThumbnailTier(effectiveOptions), effectiveOptions.maxTierWidth);
    const fitBy = isFinitePositiveNumber(asset.width) && isFinitePositiveNumber(asset.height) && asset.height! > asset.width!
      ? 'height'
      : 'width';

    return {
      url: buildVideoPosterPreviewUrl(videoSourceUrl, tier, { fitBy }),
      sourceUrl: videoSourceUrl,
      tierWidth: tier.width,
      tierHeight: tier.height,
    };
  }

  if (!asset || !previewUrl || !effectiveOptions) {
    return {
      url: previewUrl && isOssProcessableImageUrl(previewUrl) ? buildOssWatermarkUrl(previewUrl) : previewUrl,
      sourceUrl: originalUrl ?? previewUrl,
    };
  }

  if (!isFinitePositiveNumber(effectiveOptions.displayWidth) || !isFinitePositiveNumber(effectiveOptions.displayHeight)) {
    return {
      url: previewUrl && isOssProcessableImageUrl(previewUrl) ? buildOssWatermarkUrl(previewUrl) : previewUrl,
      sourceUrl: resolveAssetOriginalUrl(asset) ?? previewUrl,
    };
  }

  const ossSourceUrl = originalUrl && isOssProcessableImageUrl(originalUrl)
    ? originalUrl
    : previewUrl && isOssProcessableImageUrl(previewUrl)
      ? previewUrl
      : undefined;

  if (!ossSourceUrl) {
    return {
      url: previewUrl && isOssProcessableImageUrl(previewUrl) ? buildOssWatermarkUrl(previewUrl) : previewUrl,
      sourceUrl: originalUrl ?? previewUrl,
    };
  }

  const tier = clampOssThumbnailTierMaxWidth(resolveOssThumbnailTier(effectiveOptions), effectiveOptions.maxTierWidth);

  return {
    url: buildOssThumbnailUrl(ossSourceUrl, tier),
    sourceUrl: ossSourceUrl,
    tierWidth: tier.width,
    tierHeight: tier.height,
  };
}

/** @deprecated Use resolveAssetCanvasPreviewUrl instead */
export const resolveAssetDisplayPreviewUrl = resolveAssetCanvasPreviewUrl;

/** @deprecated Use resolveAssetCanvasPreview instead */
export const resolveAssetDisplayPreview = resolveAssetCanvasPreview;
